import { QueryCombinerType } from 'api/tasks/filter-types';
import { Translate } from 'hooks/use-translation';
import { unreachable } from 'utils/unreachable';
import type { FilterFunction } from './filter-functions/types';

export { QueryCombinerType } from 'api/tasks/filter-types';

// Normalize type to handle case-insensitive matching (e.g., 'ALL' -> 'all')
const normalizeType = (type: QueryCombinerType | string): QueryCombinerType => {
  // Already valid enum value
  if (type === QueryCombinerType.Any || type === QueryCombinerType.All) {
    return type;
  }
  // Handle uppercase variants from persisted data
  const lowercased = String(type).toLowerCase();

  if (lowercased === QueryCombinerType.Any) {
    return QueryCombinerType.Any;
  }
  if (lowercased === QueryCombinerType.All) {
    return QueryCombinerType.All;
  }

  // Return the original type to let the switch's default case handle it
  return type as QueryCombinerType;
};

export const getReducer =
  (type: QueryCombinerType) =>
  (previous: FilterFunction, current: FilterFunction): FilterFunction =>
  (record: Record<string, unknown>): boolean => {
    const normalizedType = normalizeType(type);

    switch (normalizedType) {
      case QueryCombinerType.Any:
        return previous(record) || current(record);
      case QueryCombinerType.All:
        return previous(record) && current(record);
      default:
        return unreachable(normalizedType);
    }
  };

export const getSeed = (type: QueryCombinerType): FilterFunction => {
  const normalizedType = normalizeType(type);

  switch (normalizedType) {
    case QueryCombinerType.Any:
      return () => false;
    case QueryCombinerType.All:
      return () => true;
    default:
      return unreachable(normalizedType);
  }
};

export const getText = (index: number, type: QueryCombinerType) => {
  switch (index) {
    case 0:
      return 'Where';
    default:
      switch (type) {
        case QueryCombinerType.Any:
          return 'Or';
        case QueryCombinerType.All:
          return 'And';
        default:
          return unreachable(type);
      }
  }
};

// TODO: Use translation strings
export const text = (
  index: number,
  type: QueryCombinerType,
  translate: Translate<string, string>,
) => {
  switch (index) {
    case 0:
      return translate('where');
    default:
      switch (type) {
        case QueryCombinerType.Any:
          return translate('or.capitalized');
        case QueryCombinerType.All:
          return translate('and.capitalized');
        default:
          return unreachable(type);
      }
  }
};
